import React, { useContext } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import CourseCardOverline from "../../../../components/CourseCard/CourseCardOverline";
import CourseTypeIcon from "../../../../components/Icons/CourseTypeIcon";
import Carousel from "../../../../components/Carousel/Carousel";
import Banner from "../../../../components/Banner/Banner";
import PropTypes from "prop-types";
import AssignmentDate from "../../../Assignment/AssignmentDate";
import IltClassLocation from "../../../IltClass/IltClassLocation";
import UserAssignmentContext from "../../../../context/UserAssignmentContext";
import CourseActionButton from "../../../Buttons/CourseActionButton";
import { courseTypes } from "../../../../constants";

const useStyles = makeStyles(theme => ({
  imageContainer: {
    display: "flex",
    margin: theme.spacing(0),
    height: "100%"
  },
  image: {
    minWidth: 400,
    maxWidth: 400,
    height: "100%",
    [theme.breakpoints.down("md")]: {
      minWidth: 320,
      maxWidth: 320,
      objectFit: "contain"
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
      maxHeight: `calc(100vw - ${theme.spacing(4)})`,
      objectFit: "cover"
    }
  },
  bannerButton: {
    flex: "unset"
  },
  courseName: {
    display: "-webkit-box",
    textOverflow: "ellipsis",
    overflow: "hidden",
    "-webkit-line-clamp": 1,
    "-webkit-box-orient": "vertical"
  },
  description: {
    display: "-webkit-box",
    textOverflow: "ellipsis",
    overflow: "hidden",
    "-webkit-line-clamp": 3,
    "-webkit-box-orient": "vertical",
    [theme.breakpoints.only("md")]: {
      "-webkit-line-clamp": 2
    },
    [theme.breakpoints.only("sm")]: {
      "-webkit-line-clamp": 1
    },
    [theme.breakpoints.down("sm")]: {
      "-webkit-line-clamp": "unset"
    }
  }
}));

const LatestReleaseBanner = props => {
  const {
    onStartCourse,
    onCreateUserAssignment,
    onResumeAssignment,
    isEnrolled,
    isStarted,
    isUpdatingUserAssignment
  } = useContext(UserAssignmentContext);
  const { latestReleases } = props;
  const classes = useStyles();
  const theme = useTheme();

  const handleStartClick = course => {
    if (course.userAssignment && course.courseType !== courseTypes.ilt) {
      // For online courses, automatically start the enrolled version of the course
      onResumeAssignment(course.userAssignment);
    } else {
      // allow users to enroll into a different session for ILTs
      onStartCourse(course);
    }
  };

  const bannerItems = latestReleases
    ? latestReleases.map((course, index) => {
        return (
          <Banner key={index}>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              height="100%"
              maxHeight={{ sm: 320, md: 400 }}
            >
              <img src={course.image} alt="Course" className={classes.image} />
              <Box
                display="flex"
                flexDirection="column"
                mt={{ xs: 3.5, md: 6 }}
                ml={{ xs: 4, md: 6 }}
                mr={4}
                mb={4}
                color={alpha(theme.palette.common.white, 0.7)}
              >
                <Typography variant="h3" color="white">
                  Latest Release
                </Typography>
                <Box
                  display="flex"
                  mt={{ xs: 4.5, sm: 3, md: 4.5 }}
                  mb={0.75}
                  alignItems="center"
                >
                  <Box mr={1.5}>
                    <CourseTypeIcon
                      type={course.typeName}
                      size="small"
                      color="action.active"
                    />
                  </Box>
                  <CourseCardOverline
                    courseType={course.typeName}
                    className={classes.title}
                  />
                </Box>
                <Box
                  mb={{ xs: 2, sm: 1, md: 2 }}
                  mt={{ xs: 1.25, sm: 0.25, md: 1.25 }}
                >
                  <Typography
                    variant="h4"
                    color="textPrimary"
                    className={classes.courseName}
                  >
                    {course.name}
                  </Typography>
                </Box>
                {course.scheduledDate && (
                  <Box mb={0.75}>
                    <AssignmentDate
                      scheduledDate={course.scheduledDate}
                      scheduledTime={course.scheduledTime}
                    />
                  </Box>
                )}
                {course.trainingLocation && (
                  <Box mb={0.75}>
                    <IltClassLocation
                      location={{
                        city: course.trainingLocation.city,
                        state: course.trainingLocation.state
                      }}
                      courseType={course.courseType}
                    />
                  </Box>
                )}
                {course.description && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.description}
                  >
                    {course.description}
                  </Typography>
                )}
                <Box mt={{ xs: 3, sm: 2, md: 3 }}>
                  <CourseActionButton
                    isEnrolled={isEnrolled(course)}
                    isAssigned={!!course.userAssignment}
                    isStarted={isStarted(course.userAssignment)}
                    courseType={course.courseType}
                    onEnrollClick={() => handleStartClick(course)}
                    onResumeCourseClick={() =>
                      onResumeAssignment(course?.userAssignment)
                    }
                    onAddToCoursesClick={() =>
                      onCreateUserAssignment(course, true)
                    }
                    isInstructorForCourse={course.isInstructorForCourse}
                    prereqs={course.prerequisites || []}
                    startDate={course.zonedStartDate}
                    endDate={course.zonedEndDate}
                    iltClassType={course.typeName}
                    className={classes.bannerButton}
                  />
                </Box>
              </Box>
            </Box>
          </Banner>
        );
      })
    : [];
  return (
    <Carousel
      steps={bannerItems}
      alignDots="end"
      swipeOnClick="true"
      pause={isUpdatingUserAssignment}
    />
  );
};

export default LatestReleaseBanner;

LatestReleaseBanner.propTypes = {
  latestReleases: PropTypes.any,
  startCourse: PropTypes.func,
  pause: PropTypes.bool
};
